import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Coins,
  Flag,
  ShoppingBag,
  TrendingUp,
  Search,
  Shield,
  Package,
  Receipt,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  UserCheck,
  UserX,
  AlertTriangle,
  CheckCircle,
  Clock,
  CreditCard,
  Gift,
  Plus,
  Edit,
  Trash2,
  Calendar,
  Award,
  X,
  Tag,
  Upload,
  Loader2,
  FileCheck,
  Truck,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Wrench,
  Play,
  RefreshCw,
  MessageSquare,
  Zap,
} from "lucide-react";
import {
  useAllUsers,
  useAllGTWallets,
  useReports,
  useStoreRedemptions,
  useAllGTPurchases,
  useAllGTTransactions,
  useBanUser,
  useUnbanUser,
  useAdjustGT,
  useResolveReport,
  useFulfillRedemption,
  useAdminsList,
  useAllTokenPackages,
  useSlipSubmissions,
} from "../hooks/useFirebaseData";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/common/Input";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import {
  User,
  Report,
  StoreRedemption,
  SignupOffer,
  SignupOfferRedemption,
  TokenPackage,
  ProfileTag,
} from "../types";
import { formatPrice } from "../config/stripe";
import {
  dbGet,
  dbSet,
  dbUpdate,
  dbRemove,
  subscribeToPath,
  callFunction,
  uploadFile,
} from "../config/firebase";
import { useTestMode } from "../contexts/TestModeContext";
import { TestModeWarning } from "../components/common/TestModeBanner";
import {
  format,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  startOfHour,
  startOfDay,
  eachHourOfInterval,
  eachDayOfInterval,
} from "date-fns";
import { safeFormat } from "../utils/safeFormat";

// Import extracted components
import {
  BanModal,
  AdjustGTModal,
  UserProfileModal,
  UserRow,
  TransactionRow,
  ReportRow,
  RedemptionRow,
  AnalyticsChart,
  SlipVerificationTab,
  ScheduleTab,
  StoreManagementTab,
  RedemptionManagementTab,
  TriviaManagementTab,
  SystemToolsTab,
  type TimeRange,
  type ChartMetric,
  type ExtendedTransaction,
} from "../components/admin";

type AdminTab =
  | "overview"
  | "users"
  | "transactions"
  | "reports"
  | "redemptions"
  | "offers"
  | "gtTokens"
  | "verification"
  | "schedule"
  | "store"
  | "orders"
  | "trivia"
  | "tools";

// Main Admin Page
export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [timeRange, setTimeRange] = useState<TimeRange>("daily");
  const [activeMetric, setActiveMetric] = useState<ChartMetric>("users");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Data hooks
  const { data: users, isLoading: usersLoading } = useAllUsers();
  const { data: wallets } = useAllGTWallets();
  const { data: adminsList } = useAdminsList();
  const { data: pendingReports } = useReports("pending");
  const { data: allReports } = useReports();
  const { data: pendingRedemptions } = useStoreRedemptions("pending");
  const { data: allRedemptions } = useStoreRedemptions();
  const { data: allPurchases } = useAllGTPurchases();
  const { data: allTransactions } = useAllGTTransactions();

  // Mutations
  const banUser = useBanUser();
  const unbanUser = useUnbanUser();
  const adjustGT = useAdjustGT();
  const resolveReport = useResolveReport();
  const fulfillRedemption = useFulfillRedemption();

  // Modal state
  const [banModalUser, setBanModalUser] = useState<User | null>(null);
  const [adjustGTUser, setAdjustGTUser] = useState<User | null>(null);
  const [viewProfileUser, setViewProfileUser] = useState<User | null>(null);

  // Signup Offers state
  const [signupOffers, setSignupOffers] = useState<SignupOffer[]>([]);
  const [offerRedemptions, setOfferRedemptions] = useState<
    SignupOfferRedemption[]
  >([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState<SignupOffer | null>(null);
  const [offersLoading, setOffersLoading] = useState(true);

  // Profile Tags state
  const [profileTags, setProfileTags] = useState<ProfileTag[]>([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState<ProfileTag | null>(null);

  // GT Packages state
  const { packages: gtPackages, loading: gtPackagesLoading } =
    useAllTokenPackages();
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<TokenPackage | null>(
    null
  );

  // Use test mode from context
  const { isTestMode, getPath } = useTestMode();

  // Fetch signup offers (test mode aware)
  React.useEffect(() => {
    const offersPath = getPath("config/signupOffers");
    const redemptionsPath = getPath("config/offerRedemptions");

    const unsubOffers = subscribeToPath(offersPath, (data) => {
      if (data) {
        const offers = Object.values(data) as SignupOffer[];
        setSignupOffers(offers.sort((a, b) => b.createdAt - a.createdAt));
      } else {
        setSignupOffers([]);
      }
      setOffersLoading(false);
    });

    const unsubRedemptions = subscribeToPath(redemptionsPath, (data) => {
      if (data) {
        const redemptions = Object.values(data) as SignupOfferRedemption[];
        setOfferRedemptions(
          redemptions.sort((a, b) => b.redeemedAt - a.redeemedAt)
        );
      } else {
        setOfferRedemptions([]);
      }
    });

    return () => {
      unsubOffers();
      unsubRedemptions();
    };
  }, [getPath]);

  // Fetch profile tags (test mode aware)
  React.useEffect(() => {
    const tagsPath = getPath("config/profileTags");

    const unsubTags = subscribeToPath(tagsPath, (data) => {
      if (data) {
        const tags = Object.values(data) as ProfileTag[];
        setProfileTags(
          tags
            .filter((t) => t.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder)
        );
      } else {
        setProfileTags([]);
      }
    });

    return () => {
      unsubTags();
    };
  }, [getPath]);

  // Calculate GT circulating (all time)
  const gtCirculating = useMemo(() => {
    if (!wallets) return 0;
    return Object.values(wallets).reduce(
      (sum, wallet) => sum + (wallet.balance || 0),
      0
    );
  }, [wallets]);

  // Get time boundaries based on selected range
  const getTimeBoundaries = useMemo(() => {
    const now = new Date();
    switch (timeRange) {
      case "hourly":
        return {
          start: subHours(now, 1),
          previousStart: subHours(now, 2),
          previousEnd: subHours(now, 1),
        };
      case "daily":
        return {
          start: subDays(now, 1),
          previousStart: subDays(now, 2),
          previousEnd: subDays(now, 1),
        };
      case "weekly":
        return {
          start: subWeeks(now, 1),
          previousStart: subWeeks(now, 2),
          previousEnd: subWeeks(now, 1),
        };
      case "monthly":
        return {
          start: subMonths(now, 1),
          previousStart: subMonths(now, 2),
          previousEnd: subMonths(now, 1),
        };
    }
  }, [timeRange]);

  // Calculate period-specific stats
  const periodStats = useMemo(() => {
    const { start, previousStart, previousEnd } = getTimeBoundaries;

    // User stats
    const totalUsers = users?.length ?? 0;
    const newUsers =
      users?.filter((u) => new Date(u.createdAt) >= start).length ?? 0;
    const previousNewUsers =
      users?.filter((u) => {
        const created = new Date(u.createdAt);
        return created >= previousStart && created < previousEnd;
      }).length ?? 0;
    const userChange =
      previousNewUsers > 0
        ? Math.round(((newUsers - previousNewUsers) / previousNewUsers) * 100)
        : newUsers > 0
        ? 100
        : 0;

    // Active users (had a transaction in the period)
    const activeUserIds = new Set(
      allTransactions
        ?.filter((t) => new Date(t.createdAt) >= start)
        .map((t) => t.userId) ?? []
    );
    const activeUsers = activeUserIds.size;

    // GT stats
    const periodTransactions =
      allTransactions?.filter((t) => new Date(t.createdAt) >= start) ?? [];
    const previousPeriodTransactions =
      allTransactions?.filter((t) => {
        const created = new Date(t.createdAt);
        return created >= previousStart && created < previousEnd;
      }) ?? [];

    const gtEarned = periodTransactions
      .filter((t) => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    const gtSpent = Math.abs(
      periodTransactions
        .filter((t) => t.amount < 0)
        .reduce((sum, t) => sum + t.amount, 0)
    );
    const previousGtEarned = previousPeriodTransactions
      .filter((t) => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    const gtChange =
      previousGtEarned > 0
        ? Math.round(((gtEarned - previousGtEarned) / previousGtEarned) * 100)
        : gtEarned > 0
        ? 100
        : 0;

    // Income stats
    const periodPurchases =
      allPurchases?.filter(
        (p) => p.status === "completed" && new Date(p.createdAt) >= start
      ) ?? [];
    const previousPeriodPurchases =
      allPurchases?.filter((p) => {
        const created = new Date(p.createdAt);
        return (
          p.status === "completed" &&
          created >= previousStart &&
          created < previousEnd
        );
      }) ?? [];

    const incomeTotal =
      periodPurchases.reduce((sum, p) => sum + p.amountCents, 0) / 100;
    const previousIncomeTotal =
      previousPeriodPurchases.reduce((sum, p) => sum + p.amountCents, 0) / 100;
    const incomeChange =
      previousIncomeTotal > 0
        ? Math.round(
            ((incomeTotal - previousIncomeTotal) / previousIncomeTotal) * 100
          )
        : incomeTotal > 0
        ? 100
        : 0;
    const purchaseCount = periodPurchases.length;
    const averageOrder = purchaseCount > 0 ? incomeTotal / purchaseCount : 0;

    return {
      users: {
        total: totalUsers,
        new: newUsers,
        active: activeUsers,
        change: userChange,
      },
      gt: {
        circulating: gtCirculating,
        earned: gtEarned,
        spent: gtSpent,
        change: gtChange,
      },
      income: {
        total: incomeTotal,
        purchases: purchaseCount,
        averageOrder,
        change: incomeChange,
      },
    };
  }, [users, allTransactions, allPurchases, getTimeBoundaries, gtCirculating]);

  // Generate chart data based on time range
  const chartData = useMemo(() => {
    if (!users || !allPurchases || !allTransactions) return [];

    const now = new Date();
    let intervals: Date[];
    let dateFormat: string;

    switch (timeRange) {
      case "hourly":
        intervals = eachHourOfInterval({ start: subHours(now, 24), end: now });
        dateFormat = "HH:mm";
        break;
      case "daily":
        intervals = eachDayOfInterval({ start: subDays(now, 7), end: now });
        dateFormat = "EEE";
        break;
      case "weekly":
        intervals = eachDayOfInterval({ start: subWeeks(now, 4), end: now });
        dateFormat = "MMM d";
        break;
      case "monthly":
      default:
        intervals = eachDayOfInterval({ start: subMonths(now, 1), end: now });
        dateFormat = "MMM d";
        break;
    }

    return intervals.map((date, index) => {
      const periodStart =
        timeRange === "hourly" ? startOfHour(date) : startOfDay(date);
      const periodEnd = intervals[index + 1]
        ? timeRange === "hourly"
          ? startOfHour(intervals[index + 1])
          : startOfDay(intervals[index + 1])
        : now;

      const newUsers = users.filter((u) => {
        const created = new Date(u.createdAt);
        return created >= periodStart && created < periodEnd;
      }).length;

      const gtEarned = allTransactions
        .filter((t) => {
          const created = new Date(t.createdAt);
          return created >= periodStart && created < periodEnd && t.amount > 0;
        })
        .reduce((sum, t) => sum + t.amount, 0);

      const income =
        allPurchases
          .filter((p) => {
            const created = new Date(p.createdAt);
            return (
              created >= periodStart &&
              created < periodEnd &&
              p.status === "completed"
            );
          })
          .reduce((sum, p) => sum + p.amountCents, 0) / 100;

      return {
        date: format(date, dateFormat),
        users: newUsers,
        gt: gtEarned,
        income,
      };
    });
  }, [users, allPurchases, allTransactions, timeRange]);

  // Calculate additional stats for tabs
  const userStats = useMemo(() => {
    if (!users) return { total: 0, active: 0, banned: 0, admins: 0 };
    // Count admins from the /admins path (real-time)
    const adminCount = adminsList ? Object.keys(adminsList).length : 0;
    return {
      total: users.length,
      active: users.filter((u) => !u.isBanned).length,
      banned: users.filter((u) => u.isBanned).length,
      admins: adminCount,
    };
  }, [users, adminsList]);

  const transactionStats = useMemo(() => {
    if (!allTransactions)
      return { total: 0, purchases: 0, adjustments: 0, spending: 0 };
    return {
      total: allTransactions.length,
      purchases: allTransactions.filter((t) => t.type === "purchase").length,
      adjustments: allTransactions.filter((t) => t.type === "admin_adjustment")
        .length,
      spending: allTransactions.filter((t) => t.amount < 0).length,
    };
  }, [allTransactions]);

  const reportStats = useMemo(() => {
    if (!allReports) return { total: 0, pending: 0, resolved: 0, dismissed: 0 };
    return {
      total: allReports.length,
      pending: allReports.filter((r) => r.status === "pending").length,
      resolved: allReports.filter((r) => r.status === "resolved").length,
      dismissed: allReports.filter((r) => r.status === "dismissed").length,
    };
  }, [allReports]);

  const redemptionStats = useMemo(() => {
    if (!allRedemptions)
      return { total: 0, pending: 0, fulfilled: 0, totalGtSpent: 0 };
    return {
      total: allRedemptions.length,
      pending: allRedemptions.filter((r) => r.status === "pending").length,
      fulfilled: allRedemptions.filter((r) => r.status === "fulfilled").length,
      totalGtSpent: allRedemptions.reduce(
        (sum, r) => sum + (r.gtSpent || 0),
        0
      ),
    };
  }, [allRedemptions]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        u.displayName.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  // Create users map for transactions
  const usersMap = useMemo(() => {
    if (!users) return {};
    return users.reduce(
      (acc, u) => ({ ...acc, [u.uid]: u }),
      {} as Record<string, User>
    );
  }, [users]);

  // Handlers
  const handleBanUser = async (reason: string) => {
    if (!banModalUser || !user) return;
    await banUser.mutateAsync({
      userId: banModalUser.uid,
      reason,
      bannedBy: user.uid,
    });
    setBanModalUser(null);
    setViewProfileUser(null);
  };

  const handleUnbanUser = async (targetUser: User) => {
    await unbanUser.mutateAsync(targetUser.uid);
    setViewProfileUser(null);
  };

  const handleAdjustGT = async (amount: number, description: string) => {
    if (!adjustGTUser || !user) return;
    await adjustGT.mutateAsync({
      userId: adjustGTUser.uid,
      amount,
      description,
      adminId: user.uid,
    });
    setAdjustGTUser(null);
    setViewProfileUser(null);
  };

  const handleResolveReport = async (report: Report) => {
    if (!user) return;
    await resolveReport.mutateAsync({
      reportId: report.reportId,
      action: "none",
      actionNotes: "Reviewed and dismissed",
      reviewedBy: user.uid,
      reviewedByUsername: user.username,
    });
  };

  const handleFulfillRedemption = async (redemption: StoreRedemption) => {
    if (!user) return;
    await fulfillRedemption.mutateAsync({
      redemptionId: redemption.redemptionId,
      fulfillmentCode: "FULFILLED-" + Date.now(),
      fulfilledBy: user.uid,
    });
  };

  // Offer helper functions
  const handleSaveOffer = async (offer: Partial<SignupOffer>) => {
    const now = Date.now();
    if (editingOffer) {
      // Update existing offer (explicitly set each field to avoid undefined)
      const updateData: Record<string, any> = {
        name: offer.name || editingOffer.name,
        description: offer.description || "",
        gtBonus: offer.gtBonus ?? editingOffer.gtBonus,
        tagId: offer.tagId || null,
        badgeId: offer.badgeId || null,
        badgeImageUrl: offer.badgeImageUrl || null,
        discordLink: offer.discordLink || null,
        welcomeTitle: offer.welcomeTitle || "Welcome!",
        welcomeMessage: offer.welcomeMessage || "Thank you for joining!",
        isActive: offer.isActive ?? editingOffer.isActive,
        startsAt: offer.startsAt || editingOffer.startsAt,
        expiresAt: offer.expiresAt || editingOffer.expiresAt,
        maxRedemptions: offer.maxRedemptions ?? null,
        updatedAt: now,
      };
      await dbUpdate(
        getPath(`config/signupOffers/${editingOffer.offerId}`),
        updateData
      );
    } else {
      // Create new offer
      const offerId = `offer_${now}`;
      const newOffer: Record<string, any> = {
        offerId,
        name: offer.name || "New Offer",
        description: offer.description || "",
        gtBonus: offer.gtBonus || 100,
        tagId: offer.tagId || null,
        badgeId: offer.badgeId || null,
        badgeImageUrl: offer.badgeImageUrl || null,
        discordLink: offer.discordLink || null,
        welcomeTitle: offer.welcomeTitle || "Welcome!",
        welcomeMessage: offer.welcomeMessage || "Thank you for joining!",
        isActive: offer.isActive ?? true,
        startsAt: offer.startsAt || now,
        expiresAt: offer.expiresAt || now + 365 * 24 * 60 * 60 * 1000, // 1 year default
        maxRedemptions: offer.maxRedemptions ?? null,
        currentRedemptions: 0,
        createdAt: now,
        updatedAt: now,
        createdBy: user?.uid || "admin",
      };
      await dbSet(getPath(`config/signupOffers/${offerId}`), newOffer);
    }
    setShowOfferModal(false);
    setEditingOffer(null);
  };

  const handleDeleteOffer = async (offerId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this offer? This cannot be undone."
      )
    ) {
      await dbRemove(getPath(`config/signupOffers/${offerId}`));
    }
  };

  const handleToggleOfferActive = async (offer: SignupOffer) => {
    await dbUpdate(getPath(`config/signupOffers/${offer.offerId}`), {
      isActive: !offer.isActive,
      updatedAt: Date.now(),
    });
  };

  const activeOffersCount = signupOffers.filter(
    (o) => o.isActive && Date.now() < o.expiresAt
  ).length;
  const activePackagesCount = gtPackages.filter((p) => p.isActive).length;
  const activeTagsCount = profileTags.filter((t) => t.isActive).length;

  // Profile Tag handlers
  const handleSaveTag = async (tag: Partial<ProfileTag>) => {
    const now = Date.now();
    if (editingTag) {
      // Update existing tag
      const updateData: Record<string, any> = {
        name: tag.name || editingTag.name,
        description: tag.description || "",
        color: tag.color || "#10B981",
        textColor: tag.textColor || "#000000",
        icon: tag.icon || null,
        sortOrder: tag.sortOrder ?? editingTag.sortOrder,
        isActive: tag.isActive ?? editingTag.isActive,
        updatedAt: now,
      };
      await dbUpdate(getPath(`config/profileTags/${editingTag.tagId}`), updateData);
    } else {
      // Create new tag
      const tagId = `tag_${now}`;
      const newTag: Record<string, any> = {
        tagId,
        name: tag.name || "New Tag",
        description: tag.description || "",
        color: tag.color || "#10B981",
        textColor: tag.textColor || "#000000",
        icon: tag.icon || null,
        sortOrder: tag.sortOrder ?? profileTags.length + 1,
        isActive: tag.isActive ?? true,
        createdAt: now,
        updatedAt: now,
      };
      await dbSet(getPath(`config/profileTags/${tagId}`), newTag);
    }
    setShowTagModal(false);
    setEditingTag(null);
  };

  const handleDeleteTag = async (tagId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this tag? Users who have earned it will keep it."
      )
    ) {
      await dbRemove(getPath(`config/profileTags/${tagId}`));
    }
  };

  const handleToggleTagActive = async (tag: ProfileTag) => {
    await dbUpdate(getPath(`config/profileTags/${tag.tagId}`), {
      isActive: !tag.isActive,
      updatedAt: Date.now(),
    });
  };

  // GT Package handlers
  const handleSavePackage = async (pkg: Partial<TokenPackage>) => {
    const now = Date.now();

    try {
      if (editingPackage) {
        // Update existing package - call Stripe function first
        const manageStripeProduct = callFunction<any, any>(
          "manageStripeProduct"
        );
        const result = await manageStripeProduct({
          action: "update",
          packageData: {
            packageId: editingPackage.packageId,
            name: pkg.name,
            description: pkg.description,
            tokens: pkg.tokens,
            bonusTokens: pkg.bonusTokens,
            priceUsdCents: pkg.priceUsdCents,
            stripeProductId: editingPackage.stripeProductId,
            stripePriceId: editingPackage.stripePriceId,
          },
          dev: isTestMode,
        });
        const stripeResult = result.data;

        // Update Firebase with new Stripe IDs (filter out undefined values)
        const updateData: Record<string, any> = {
          name: pkg.name,
          tokens: pkg.tokens,
          bonusTokens: pkg.bonusTokens,
          priceUsdCents: pkg.priceUsdCents,
          isActive: pkg.isActive,
          sortOrder: pkg.sortOrder,
          description: pkg.description || "",
          badge: pkg.badge || null,
          stripeProductId:
            stripeResult.stripeProductId || editingPackage.stripeProductId,
          stripePriceId:
            stripeResult.stripePriceId || editingPackage.stripePriceId,
          updatedAt: now,
        };
        await dbUpdate(
          getPath(`wallet/packages/${editingPackage.packageId}`),
          updateData
        );
      } else {
        // Create new package - call Stripe function first
        const manageStripeProduct = callFunction<any, any>(
          "manageStripeProduct"
        );
        const result = await manageStripeProduct({
          action: "create",
          packageData: {
            name: pkg.name || "New Package",
            description: pkg.description || "",
            tokens: pkg.tokens || 500,
            bonusTokens: pkg.bonusTokens || 0,
            priceUsdCents: pkg.priceUsdCents || 499,
          },
          dev: isTestMode,
        });
        const stripeResult = result.data;

        // Create new package in Firebase with Stripe IDs
        const packageId = `pkg_${now}`;
        const newPackage: Record<string, any> = {
          packageId,
          name: pkg.name || "New Package",
          tokens: pkg.tokens || 500,
          bonusTokens: pkg.bonusTokens || 0,
          priceUsdCents: pkg.priceUsdCents || 499,
          stripePriceId: stripeResult.stripePriceId || "",
          stripeProductId: stripeResult.stripeProductId || "",
          isActive: pkg.isActive ?? true,
          sortOrder: pkg.sortOrder || gtPackages.length + 1,
          description: pkg.description || "",
          badge: pkg.badge || null,
          createdAt: now,
          updatedAt: now,
        };
        await dbSet(getPath(`wallet/packages/${packageId}`), newPackage);
      }
    } catch (error) {
      console.error("Failed to save package:", error);
      alert("Failed to save package. Please check console for details.");
      return;
    }

    setShowPackageModal(false);
    setEditingPackage(null);
  };

  const handleDeletePackage = async (packageId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this package? This cannot be undone."
      )
    ) {
      return;
    }

    const pkg = gtPackages.find((p) => p.packageId === packageId);

    try {
      // Archive on Stripe first
      if (pkg?.stripeProductId || pkg?.stripePriceId) {
        const manageStripeProduct = callFunction<any, any>(
          "manageStripeProduct"
        );
        await manageStripeProduct({
          action: "delete",
          packageData: {
            stripeProductId: pkg.stripeProductId,
            stripePriceId: pkg.stripePriceId,
          },
          dev: isTestMode,
        });
      }

      // Delete from Firebase
      await dbRemove(getPath(`wallet/packages/${packageId}`));
    } catch (error) {
      console.error("Failed to delete package:", error);
      alert("Failed to delete package. Please check console for details.");
    }
  };

  const handleTogglePackageActive = async (pkg: TokenPackage) => {
    await dbUpdate(getPath(`wallet/packages/${pkg.packageId}`), {
      isActive: !pkg.isActive,
      updatedAt: Date.now(),
    });
  };

  // Get pending verification submissions count
  const { submissions: pendingVerifications } = useSlipSubmissions("pending");
  const pendingVerificationCount = pendingVerifications?.length || 0;

  const tabs: {
    id: AdminTab;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }[] = [
    { id: "overview", label: "Overview", icon: <TrendingUp size={18} /> },
    { id: "users", label: "Users", icon: <Users size={18} /> },
    { id: "transactions", label: "Transactions", icon: <Receipt size={18} /> },
    {
      id: "reports",
      label: "Reports",
      icon: <Flag size={18} />,
      badge: pendingReports?.length,
    },
    {
      id: "redemptions",
      label: "Redemptions",
      icon: <ShoppingBag size={18} />,
      badge: pendingRedemptions?.length,
    },
    {
      id: "offers",
      label: "Offers",
      icon: <Gift size={18} />,
    },
    {
      id: "gtTokens",
      label: "GT Tokens",
      icon: <Coins size={18} />,
    },
    {
      id: "schedule",
      label: "Schedule",
      icon: <Calendar size={18} />,
    },
    {
      id: "store",
      label: "Store",
      icon: <ShoppingBag size={18} />,
    },
    {
      id: "orders",
      label: "Orders",
      icon: <Truck size={18} />,
      badge: pendingRedemptions?.length,
    },
    {
      id: "trivia",
      label: "Trivia",
      icon: <HelpCircle size={18} />,
    },
    {
      id: "tools",
      label: "System Tools",
      icon: <Wrench size={18} />,
    },
  ];

  return (
    <div className="flex min-h-[calc(100vh-80px)]">
      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{ width: sidebarCollapsed ? 64 : 240 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="flex-shrink-0 bg-surface-light dark:bg-surface-dark border-r border-gray-200 dark:border-gray-800 flex flex-col"
      >
        {/* Sidebar Header */}
        <div className={`p-4 border-b border-gray-200 dark:border-gray-800 ${sidebarCollapsed ? 'px-3' : ''}`}>
          <div className="flex items-center gap-3">
            <div className={`${sidebarCollapsed ? 'w-10 h-10' : 'w-10 h-10'} bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0`}>
              <Shield size={20} className="text-primary" />
            </div>
            {!sidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="overflow-hidden"
              >
                <h1 className="font-bold text-text-primary-light dark:text-text-primary-dark whitespace-nowrap">
                  Admin Panel
                </h1>
              </motion.div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200
                ${
                  activeTab === tab.id
                    ? "bg-primary/10 text-primary"
                    : "text-text-secondary-light dark:text-text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-text-primary-light dark:hover:text-text-primary-dark"
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              title={tab.label}
            >
              <span className="flex-shrink-0 relative">
                {tab.icon}
                {/* Badge dot for collapsed state */}
                {sidebarCollapsed && tab.badge && tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </span>
              {!sidebarCollapsed && (
                <>
                  <span className="flex-1 text-left text-sm">{tab.label}</span>
                  {tab.badge && tab.badge > 0 && (
                    <span className="flex min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-semibold rounded-full items-center justify-center">
                      {tab.badge > 99 ? "99+" : tab.badge}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-2 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-secondary-light dark:text-text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <PanelLeft size={18} className="mx-auto" />
            ) : (
              <>
                <PanelLeftClose size={18} />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        {/* Tab Content */}
        <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Test Mode Indicator */}
            {isTestMode && (
              <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={16} style={{ color: '#92400E', flexShrink: 0 }} />
                <div>
                  <strong style={{ color: '#92400E' }}>Test Mode Active</strong>
                  <span style={{ color: '#92400E', marginLeft: 8 }}>Showing test data only. Toggle off to see production data.</span>
                </div>
              </div>
            )}

            {/* Time Range Toggle & Key Stats */}
            <div className="card p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h3 className="text-lg font-bold">Overview</h3>
                <div className="flex bg-surface-light dark:bg-gray-800 rounded-lg p-1">
                  {(
                    ["hourly", "daily", "weekly", "monthly"] as TimeRange[]
                  ).map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        timeRange === range
                          ? "bg-white dark:bg-gray-700 shadow-sm"
                          : "text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light"
                      }`}
                    >
                      {range.charAt(0).toUpperCase() + range.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Key Stats Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      New Users
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">
                      {periodStats.users.new.toLocaleString()}
                    </p>
                    {periodStats.users.change !== 0 && (
                      <span
                        className={`flex items-center text-xs font-medium ${
                          periodStats.users.change >= 0
                            ? "text-success"
                            : "text-error"
                        }`}
                      >
                        {periodStats.users.change >= 0 ? (
                          <ArrowUpRight size={12} />
                        ) : (
                          <ArrowDownRight size={12} />
                        )}
                        {Math.abs(periodStats.users.change)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      GT Earned
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold text-success">
                      +{periodStats.gt.earned.toLocaleString()}
                    </p>
                    {periodStats.gt.change !== 0 && (
                      <span
                        className={`flex items-center text-xs font-medium ${
                          periodStats.gt.change >= 0
                            ? "text-success"
                            : "text-error"
                        }`}
                      >
                        {periodStats.gt.change >= 0 ? (
                          <ArrowUpRight size={12} />
                        ) : (
                          <ArrowDownRight size={12} />
                        )}
                        {Math.abs(periodStats.gt.change)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-info" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Revenue
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">
                      £{periodStats.income.total.toLocaleString()}
                    </p>
                    {periodStats.income.change !== 0 && (
                      <span
                        className={`flex items-center text-xs font-medium ${
                          periodStats.income.change >= 0
                            ? "text-success"
                            : "text-error"
                        }`}
                      >
                        {periodStats.income.change >= 0 ? (
                          <ArrowUpRight size={12} />
                        ) : (
                          <ArrowDownRight size={12} />
                        )}
                        {Math.abs(periodStats.income.change)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Flag size={16} className="text-warning" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Pending Actions
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {(pendingReports?.length ?? 0) +
                      (pendingRedemptions?.length ?? 0)}
                  </p>
                </div>
              </div>
            </div>

            {/* Analytics Chart with Metric Tabs */}
            <AnalyticsChart
              data={chartData}
              activeMetric={activeMetric}
              onMetricChange={setActiveMetric}
              periodStats={periodStats}
            />
          </motion.div>
        )}

        {activeTab === "verification" && <SlipVerificationTab />}

        {activeTab === "schedule" && <ScheduleTab />}

        {activeTab === "store" && <StoreManagementTab />}

        {activeTab === "orders" && <RedemptionManagementTab />}

        {activeTab === "trivia" && <TriviaManagementTab />}

        {activeTab === "tools" && <SystemToolsTab />}

        {activeTab === "users" && (
          <motion.div
            key="users"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Test Mode Indicator */}
            {isTestMode && (
              <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={16} style={{ color: '#92400E', flexShrink: 0 }} />
                <div>
                  <strong style={{ color: '#92400E' }}>Test Mode Active</strong>
                  <span style={{ color: '#92400E', marginLeft: 8 }}>Showing test users only. Admin count always reflects production.</span>
                </div>
              </div>
            )}

            {/* Users Stats */}
            <div className="card p-5">
              <h3 className="text-lg font-bold mb-4">{isTestMode ? 'Test User Statistics' : 'User Statistics'}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Users
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {userStats.total.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Active Users
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {userStats.active.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <UserX size={16} className="text-error" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Banned Users
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-error">
                    {userStats.banned.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={16} className="text-info" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Admins {isTestMode && <span className="text-warning">(prod)</span>}
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {userStats.admins.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={<Search size={18} />}
                />
              </div>
            </div>
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-light dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Balance
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center">
                          <LoadingSpinner size="lg" className="mx-auto" />
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-12 text-center text-text-secondary-light dark:text-text-secondary-dark"
                        >
                          {isTestMode ? (
                            <div className="space-y-2">
                              <Users size={32} className="mx-auto mb-2 opacity-50" />
                              <p className="font-medium">No test users found</p>
                              <p className="text-sm">Create test accounts to populate this view, or switch to production mode to see real users.</p>
                            </div>
                          ) : (
                            searchQuery ? 'No users matching your search' : 'No users found'
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <UserRow
                          key={u.uid}
                          user={u}
                          wallet={wallets?.[u.uid]}
                          onBan={setBanModalUser}
                          onUnban={handleUnbanUser}
                          onAdjustGT={setAdjustGTUser}
                          onViewProfile={setViewProfileUser}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "transactions" && (
          <motion.div
            key="transactions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Transaction Stats */}
            <div className="card p-5">
              <h3 className="text-lg font-bold mb-4">Transaction Statistics</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Transactions
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {transactionStats.total.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Purchases
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {transactionStats.purchases.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={16} className="text-warning" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Admin Adjustments
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {transactionStats.adjustments.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins size={16} className="text-error" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Spending Transactions
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-error">
                    {transactionStats.spending.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-light dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Links
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!allTransactions || allTransactions.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-12 text-center text-text-secondary-light dark:text-text-secondary-dark"
                        >
                          <Receipt
                            size={32}
                            className="mx-auto mb-2 opacity-50"
                          />
                          No transactions
                        </td>
                      </tr>
                    ) : (
                      allTransactions
                        .slice(0, 100)
                        .map((tx) => (
                          <TransactionRow
                            key={tx.transactionId}
                            transaction={tx as ExtendedTransaction}
                            user={usersMap[tx.userId]}
                            onViewUser={setViewProfileUser}
                          />
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "reports" && (
          <motion.div
            key="reports"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Reports Stats */}
            <div className="card p-5">
              <h3 className="text-lg font-bold mb-4">Report Statistics</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Flag size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Reports
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {reportStats.total.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-warning" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Pending Review
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-warning">
                    {reportStats.pending.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Resolved
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {reportStats.resolved.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle
                      size={16}
                      className="text-text-secondary-light"
                    />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Dismissed
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {reportStats.dismissed.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-light dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Reason
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Reported By
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!pendingReports || pendingReports.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-12 text-center text-text-secondary-light dark:text-text-secondary-dark"
                        >
                          <Flag size={32} className="mx-auto mb-2 opacity-50" />
                          No pending reports
                        </td>
                      </tr>
                    ) : (
                      pendingReports.map((report) => (
                        <ReportRow
                          key={report.reportId}
                          report={report}
                          onResolve={handleResolveReport}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "redemptions" && (
          <motion.div
            key="redemptions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Redemptions Stats */}
            <div className="card p-5">
              <h3 className="text-lg font-bold mb-4">Redemption Statistics</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Package size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Orders
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {redemptionStats.total.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-warning" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Pending Fulfillment
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-warning">
                    {redemptionStats.pending.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Fulfilled
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {redemptionStats.fulfilled.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins size={16} className="text-info" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total GT Spent
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {redemptionStats.totalGtSpent.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-light dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Cost
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!pendingRedemptions || pendingRedemptions.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-12 text-center text-text-secondary-light dark:text-text-secondary-dark"
                        >
                          <Package
                            size={32}
                            className="mx-auto mb-2 opacity-50"
                          />
                          No pending redemptions
                        </td>
                      </tr>
                    ) : (
                      pendingRedemptions.map((redemption) => (
                        <RedemptionRow
                          key={redemption.redemptionId}
                          redemption={redemption}
                          onFulfill={handleFulfillRedemption}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "offers" && (
          <motion.div
            key="offers"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Offers Stats & Actions */}
            <div className="card p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-bold">Signup Offers</h3>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Manage promotional offers for new users
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingOffer(null);
                    setShowOfferModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus size={18} />
                  Create Offer
                </button>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Offers
                    </p>
                  </div>
                  <p className="text-2xl font-bold">{signupOffers.length}</p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Active Offers
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {activeOffersCount}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-info" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Redemptions
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {offerRedemptions.length.toLocaleString()}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins size={16} className="text-warning" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      GT Awarded
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {offerRedemptions
                      .reduce((sum, r) => sum + r.gtAwarded, 0)
                      .toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Offers List */}
            <div className="card overflow-hidden">
              {offersLoading ? (
                <div className="p-12 text-center">
                  <LoadingSpinner />
                </div>
              ) : signupOffers.length === 0 ? (
                <div className="p-12 text-center text-text-secondary-light dark:text-text-secondary-dark">
                  <Gift size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No signup offers yet</p>
                  <p className="text-sm mt-1">
                    Create your first offer to reward new users
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border-light dark:divide-border-dark">
                  {signupOffers.map((offer) => {
                    const isExpired = Date.now() >= offer.expiresAt;
                    const isActive = offer.isActive && !isExpired;
                    // Count actual redemptions from the redemptions data
                    const actualRedemptionCount = offerRedemptions.filter(
                      (r) => r.offerId === offer.offerId
                    ).length;
                    const isLimitReached =
                      offer.maxRedemptions !== null &&
                      actualRedemptionCount >= offer.maxRedemptions;

                    return (
                      <div
                        key={offer.offerId}
                        className="p-4 hover:bg-surface-light dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          {/* Offer Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark truncate">
                                {offer.name}
                              </h4>
                              {isActive && !isLimitReached ? (
                                <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                                  Active
                                </span>
                              ) : isExpired ? (
                                <span className="px-2 py-0.5 bg-gray-500/10 text-gray-500 text-xs font-medium rounded-full">
                                  Expired
                                </span>
                              ) : isLimitReached ? (
                                <span className="px-2 py-0.5 bg-warning/10 text-warning text-xs font-medium rounded-full">
                                  Limit Reached
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-gray-500/10 text-gray-500 text-xs font-medium rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                              {offer.description || "No description"}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                              <span className="flex items-center gap-1">
                                <Coins size={12} className="text-primary" />
                                {offer.gtBonus.toLocaleString()} GT
                              </span>
                              {offer.tagId && (
                                <span className="flex items-center gap-1">
                                  <Tag size={12} className="text-primary" />
                                  {profileTags.find(
                                    (t) => t.tagId === offer.tagId
                                  )?.name || offer.tagId}
                                </span>
                              )}
                              {offer.badgeId && (
                                <span className="flex items-center gap-1">
                                  <Award size={12} className="text-primary" />
                                  {offer.badgeId}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                Expires{" "}
                                {safeFormat(
                                  offer.expiresAt,
                                  "MMM d, yyyy"
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users size={12} />
                                {actualRedemptionCount}
                                {offer.maxRedemptions
                                  ? `/${offer.maxRedemptions}`
                                  : ""}{" "}
                                redeemed
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleOfferActive(offer)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                offer.isActive
                                  ? "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20"
                                  : "bg-success/10 text-success hover:bg-success/20"
                              }`}
                            >
                              {offer.isActive ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              onClick={() => {
                                setEditingOffer(offer);
                                setShowOfferModal(true);
                              }}
                              className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteOffer(offer.offerId)}
                              className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent Redemptions */}
            {offerRedemptions.length > 0 && (
              <div className="card p-5">
                <h3 className="text-lg font-bold mb-4">Recent Redemptions</h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {offerRedemptions.slice(0, 10).map((redemption) => (
                    <div
                      key={redemption.redemptionId}
                      className="flex items-center justify-between p-3 bg-surface-light dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            @{redemption.username}
                          </p>
                          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            {redemption.offerName}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-primary">
                          +{redemption.gtAwarded.toLocaleString()} GT
                        </p>
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          {safeFormat(
                            redemption.redeemedAt,
                            "MMM d, HH:mm"
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Profile Tags Management */}
            <div className="card p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-bold">Profile Tags</h3>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Manage tags that can be awarded to users via signup offers
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingTag(null);
                    setShowTagModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus size={18} />
                  Create Tag
                </button>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Tags
                    </p>
                  </div>
                  <p className="text-2xl font-bold">{profileTags.length}</p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Active Tags
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {activeTagsCount}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift size={16} className="text-info" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Used in Offers
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {signupOffers.filter((o) => o.tagId).length}
                  </p>
                </div>
              </div>

              {/* Tags List */}
              {profileTags.length === 0 ? (
                <div className="p-8 text-center text-text-secondary-light dark:text-text-secondary-dark">
                  <Tag size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No profile tags yet</p>
                  <p className="text-sm mt-1">
                    Create your first tag to award to users
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border-light dark:divide-border-dark">
                  {profileTags.map((tag) => (
                    <div
                      key={tag.tagId}
                      className="py-3 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: tag.color,
                            color: tag.textColor,
                          }}
                        >
                          {tag.name}
                        </span>
                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {tag.description}
                        </span>
                        {!tag.isActive && (
                          <span className="px-2 py-0.5 bg-gray-500/10 text-gray-500 text-xs font-medium rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleTagActive(tag)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            tag.isActive
                              ? "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20"
                              : "bg-success/10 text-success hover:bg-success/20"
                          }`}
                        >
                          {tag.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          onClick={() => {
                            setEditingTag(tag);
                            setShowTagModal(true);
                          }}
                          className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag.tagId)}
                          className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === "gtTokens" && (
          <motion.div
            key="gtTokens"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* GT Packages Stats & Actions */}
            <div className="card p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-bold">GT Token Packages</h3>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Manage purchasable token packages{" "}
                    {isTestMode && (
                      <span className="text-warning">(Test Mode)</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingPackage(null);
                    setShowPackageModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus size={18} />
                  Create Package
                </button>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Package size={16} className="text-primary" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total Packages
                    </p>
                  </div>
                  <p className="text-2xl font-bold">{gtPackages.length}</p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-success" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Active Packages
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-success">
                    {activePackagesCount}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-info" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Price Range
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {gtPackages.length > 0
                      ? `${formatPrice(
                          Math.min(...gtPackages.map((p) => p.priceUsdCents))
                        )} - ${formatPrice(
                          Math.max(...gtPackages.map((p) => p.priceUsdCents))
                        )}`
                      : "-"}
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins size={16} className="text-warning" />
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Total GT Available
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {gtPackages
                      .filter((p) => p.isActive)
                      .reduce((sum, p) => sum + p.tokens + p.bonusTokens, 0)
                      .toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Packages List */}
            <div className="card overflow-hidden">
              {gtPackagesLoading ? (
                <div className="p-12 text-center">
                  <LoadingSpinner />
                </div>
              ) : gtPackages.length === 0 ? (
                <div className="p-12 text-center text-text-secondary-light dark:text-text-secondary-dark">
                  <Coins size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No packages configured</p>
                  <p className="text-sm mt-1">
                    Create your first GT package to enable purchases
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-light dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                          Package
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                          Tokens
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                          Price
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light dark:divide-border-dark">
                      {gtPackages.map((pkg) => (
                        <tr
                          key={pkg.packageId}
                          className="hover:bg-surface-light dark:hover:bg-gray-800/50"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Coins size={20} className="text-primary" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                                    {pkg.name}
                                  </p>
                                  {pkg.badge && (
                                    <span className="px-2 py-0.5 bg-primary text-black text-xs font-medium rounded-full">
                                      {pkg.badge}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                  {pkg.description || "No description"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium">
                                {pkg.tokens.toLocaleString()} GT
                              </p>
                              {pkg.bonusTokens > 0 && (
                                <p className="text-sm text-success">
                                  +{pkg.bonusTokens.toLocaleString()} bonus
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-medium">
                              {formatPrice(pkg.priceUsdCents)}
                            </p>
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                              $
                              {(
                                pkg.priceUsdCents /
                                (pkg.tokens + pkg.bonusTokens) /
                                100
                              ).toFixed(4)}
                              /GT
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            {pkg.isActive ? (
                              <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-500/10 text-gray-500 text-xs font-medium rounded-full">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleTogglePackageActive(pkg)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                  pkg.isActive
                                    ? "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20"
                                    : "bg-success/10 text-success hover:bg-success/20"
                                }`}
                              >
                                {pkg.isActive ? "Deactivate" : "Activate"}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingPackage(pkg);
                                  setShowPackageModal(true);
                                }}
                                className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() =>
                                  handleDeletePackage(pkg.packageId)
                                }
                                className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="card p-5 bg-info/5 border border-info/20">
              <div className="flex items-start gap-3">
                <AlertTriangle
                  size={20}
                  className="text-info flex-shrink-0 mt-0.5"
                />
                <div>
                  <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                    Stripe Integration
                  </h4>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    When creating or editing packages, make sure the Stripe
                    Price ID matches a valid price in your Stripe dashboard. The
                    payment system uses this ID to process transactions.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <AnimatePresence>
        {banModalUser && (
          <BanModal
            user={banModalUser}
            onClose={() => setBanModalUser(null)}
            onConfirm={handleBanUser}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {adjustGTUser && (
          <AdjustGTModal
            user={adjustGTUser}
            wallet={wallets?.[adjustGTUser.uid]}
            onClose={() => setAdjustGTUser(null)}
            onConfirm={handleAdjustGT}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewProfileUser && (
          <UserProfileModal
            user={viewProfileUser}
            wallet={wallets?.[viewProfileUser.uid]}
            onClose={() => setViewProfileUser(null)}
            onAdjustGT={() => setAdjustGTUser(viewProfileUser)}
            onBan={() => setBanModalUser(viewProfileUser)}
            onUnban={() => handleUnbanUser(viewProfileUser)}
          />
        )}
      </AnimatePresence>

      {/* Offer Modal */}
      <AnimatePresence>
        {showOfferModal && (
          <OfferModal
            offer={editingOffer}
            availableTags={profileTags}
            onClose={() => {
              setShowOfferModal(false);
              setEditingOffer(null);
            }}
            onSave={handleSaveOffer}
          />
        )}
      </AnimatePresence>

      {/* Package Modal */}
      <AnimatePresence>
        {showPackageModal && (
          <PackageModal
            pkg={editingPackage}
            onClose={() => {
              setShowPackageModal(false);
              setEditingPackage(null);
            }}
            onSave={handleSavePackage}
          />
        )}
      </AnimatePresence>

      {/* Tag Modal */}
      <AnimatePresence>
        {showTagModal && (
          <TagModal
            tag={editingTag}
            onClose={() => {
              setShowTagModal(false);
              setEditingTag(null);
            }}
            onSave={handleSaveTag}
          />
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

// Offer Modal Component
const OfferModal: React.FC<{
  offer: SignupOffer | null;
  availableTags: ProfileTag[];
  onClose: () => void;
  onSave: (offer: Partial<SignupOffer>) => void;
}> = ({ offer, availableTags, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<SignupOffer>>({
    name: offer?.name || "",
    description: offer?.description || "",
    gtBonus: offer?.gtBonus || 1000,
    tagId: offer?.tagId || "",
    badgeId: offer?.badgeId || "",
    badgeImageUrl: offer?.badgeImageUrl || "",
    discordLink: offer?.discordLink || "",
    welcomeTitle: offer?.welcomeTitle || "Welcome!",
    welcomeMessage:
      offer?.welcomeMessage || "Thank you for joining our community!",
    isActive: offer?.isActive ?? true,
    startsAt: offer?.startsAt || Date.now(),
    expiresAt: offer?.expiresAt || Date.now() + 365 * 24 * 60 * 60 * 1000,
    maxRedemptions: offer?.maxRedemptions || null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setUploadError("Please select an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("Image must be less than 2MB");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      // Upload to a single location that gets replaced
      const storagePath = `offer-badges/badge-image.${file.name
        .split(".")
        .pop()}`;
      const downloadUrl = await uploadFile(storagePath, file);
      setFormData({ ...formData, badgeImageUrl: downloadUrl });
    } catch (err: any) {
      console.error("Upload failed:", err);
      setUploadError(err.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const formatDateForInput = (timestamp: number) => {
    return new Date(timestamp).toISOString().slice(0, 16);
  };

  const parseDateFromInput = (value: string) => {
    return new Date(value).getTime();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border-light dark:border-border-dark">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
              {offer ? "Edit Offer" : "Create New Offer"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Test Mode Warning */}
          <TestModeWarning
            action={offer ? "Editing this offer" : "Creating this offer"}
          />

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Offer Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., Founding Member"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={2}
              placeholder="Short description of this offer"
            />
          </div>

          {/* GT Bonus */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              GT Bonus *
            </label>
            <input
              type="number"
              required
              min={0}
              value={formData.gtBonus}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  gtBonus: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Profile Tag */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Profile Tag
            </label>
            <select
              value={formData.tagId || ""}
              onChange={(e) =>
                setFormData({ ...formData, tagId: e.target.value || null })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">No tag</option>
              {availableTags.map((tag) => (
                <option key={tag.tagId} value={tag.tagId}>
                  {tag.name} - {tag.description}
                </option>
              ))}
            </select>
            {formData.tagId &&
              availableTags.find((t) => t.tagId === formData.tagId) && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    Preview:
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor:
                        availableTags.find((t) => t.tagId === formData.tagId)
                          ?.color || "#10B981",
                      color:
                        availableTags.find((t) => t.tagId === formData.tagId)
                          ?.textColor || "#000000",
                    }}
                  >
                    {
                      availableTags.find((t) => t.tagId === formData.tagId)
                        ?.name
                    }
                  </span>
                </div>
              )}
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
              Users who redeem this offer will receive this tag on their profile
            </p>
          </div>

          {/* Badge ID */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Badge ID
            </label>
            <input
              type="text"
              value={formData.badgeId || ""}
              onChange={(e) =>
                setFormData({ ...formData, badgeId: e.target.value || null })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., founding-member"
            />
          </div>

          {/* Badge Image Upload */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Badge Image
            </label>
            <div className="space-y-3">
              {/* Current Image Preview */}
              {formData.badgeImageUrl && (
                <div className="flex items-center gap-3 p-3 bg-surface-light dark:bg-gray-800 rounded-lg">
                  <img
                    src={formData.badgeImageUrl}
                    alt="Badge preview"
                    className="w-16 h-16 object-contain rounded-lg bg-white dark:bg-gray-700"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark truncate">
                      Current badge image
                    </p>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
                      {formData.badgeImageUrl}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, badgeImageUrl: "" })
                    }
                    className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}

              {/* Upload Area */}
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                />
                <div
                  className={`flex items-center justify-center gap-3 p-4 border-2 border-dashed rounded-lg transition-colors ${
                    uploading
                      ? "border-primary bg-primary/5"
                      : "border-border-light dark:border-border-dark hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  {uploading ? (
                    <>
                      <Loader2
                        size={20}
                        className="text-primary animate-spin"
                      />
                      <span className="text-sm text-primary font-medium">
                        Uploading...
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload
                        size={20}
                        className="text-text-secondary-light dark:text-text-secondary-dark"
                      />
                      <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {formData.badgeImageUrl
                          ? "Replace image"
                          : "Click to upload badge image"}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {uploadError && (
                <p className="text-xs text-error">{uploadError}</p>
              )}

              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                PNG, JPG or GIF up to 2MB. Image will be stored in Firebase
                Storage.
              </p>
            </div>
          </div>

          {/* Discord Link */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Discord Link
            </label>
            <input
              type="url"
              value={formData.discordLink || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  discordLink: e.target.value || null,
                })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="https://discord.gg/..."
            />
          </div>

          {/* Welcome Title & Message */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Welcome Title
            </label>
            <input
              type="text"
              value={formData.welcomeTitle}
              onChange={(e) =>
                setFormData({ ...formData, welcomeTitle: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Welcome, Founding Member!"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Welcome Message
            </label>
            <textarea
              value={formData.welcomeMessage}
              onChange={(e) =>
                setFormData({ ...formData, welcomeMessage: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
              placeholder="Thank you for being an early supporter..."
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Starts At
              </label>
              <input
                type="datetime-local"
                value={formatDateForInput(formData.startsAt || Date.now())}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    startsAt: parseDateFromInput(e.target.value),
                  })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Expires At
              </label>
              <input
                type="datetime-local"
                value={formatDateForInput(formData.expiresAt || Date.now())}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expiresAt: parseDateFromInput(e.target.value),
                  })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Max Redemptions */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Max Redemptions (leave empty for unlimited)
            </label>
            <input
              type="number"
              min={0}
              value={formData.maxRedemptions || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxRedemptions: e.target.value
                    ? parseInt(e.target.value)
                    : null,
                })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Unlimited"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="w-4 h-4 text-primary bg-surface-light dark:bg-gray-800 border-border-light dark:border-border-dark rounded focus:ring-primary"
            />
            <label
              htmlFor="isActive"
              className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark"
            >
              Offer is active
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border-light dark:border-border-dark rounded-lg font-medium hover:bg-surface-light dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-black rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : offer ? "Update Offer" : "Create Offer"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Package Modal Component
const PackageModal: React.FC<{
  pkg: TokenPackage | null;
  onClose: () => void;
  onSave: (pkg: Partial<TokenPackage>) => void;
}> = ({ pkg, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<TokenPackage>>({
    name: pkg?.name || "",
    tokens: pkg?.tokens || 500,
    bonusTokens: pkg?.bonusTokens || 0,
    priceUsdCents: pkg?.priceUsdCents || 499,
    stripePriceId: pkg?.stripePriceId || "",
    stripeProductId: pkg?.stripeProductId || "",
    isActive: pkg?.isActive ?? true,
    sortOrder: pkg?.sortOrder || 1,
    description: pkg?.description || "",
    badge: pkg?.badge || "",
  });
  const [saving, setSaving] = useState(false);

  // Convert cents to dollars for display
  const priceInDollars = (formData.priceUsdCents || 0) / 100;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border-light dark:border-border-dark">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
              {pkg ? "Edit Package" : "Create New Package"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Test Mode Warning */}
          <TestModeWarning
            action={pkg ? "Editing this package" : "Creating this package"}
          />

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Package Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., Starter Pack"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Short description"
            />
          </div>

          {/* Tokens & Bonus */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Base Tokens *
              </label>
              <input
                type="number"
                required
                min={1}
                value={formData.tokens}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tokens: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Bonus Tokens
              </label>
              <input
                type="number"
                min={0}
                value={formData.bonusTokens}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bonusTokens: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Price (USD) *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary-light dark:text-text-secondary-dark">
                $
              </span>
              <input
                type="number"
                required
                min={0.01}
                step={0.01}
                value={priceInDollars}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priceUsdCents:
                      Math.round(parseFloat(e.target.value) * 100) || 0,
                  })
                }
                className="w-full pl-8 pr-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
              {formData.tokens && formData.tokens > 0
                ? `$${(
                    (formData.priceUsdCents || 0) /
                    ((formData.tokens || 0) + (formData.bonusTokens || 0)) /
                    100
                  ).toFixed(4)} per GT`
                : "-"}
            </p>
          </div>

          {/* Stripe IDs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Stripe Product ID
              </label>
              <input
                type="text"
                value={formData.stripeProductId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, stripeProductId: e.target.value })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                placeholder="prod_XXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Stripe Price ID
              </label>
              <input
                type="text"
                value={formData.stripePriceId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, stripePriceId: e.target.value })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                placeholder="price_XXX"
              />
            </div>
          </div>

          {/* Badge & Sort Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Badge Label
              </label>
              <input
                type="text"
                value={formData.badge || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    badge: e.target.value || undefined,
                  })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="e.g., Popular, Best Value"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Sort Order
              </label>
              <input
                type="number"
                min={1}
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sortOrder: parseInt(e.target.value) || 1,
                  })
                }
                className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="pkgIsActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="w-4 h-4 text-primary bg-surface-light dark:bg-gray-800 border-border-light dark:border-border-dark rounded focus:ring-primary"
            />
            <label
              htmlFor="pkgIsActive"
              className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark"
            >
              Package is active
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border-light dark:border-border-dark rounded-lg font-medium hover:bg-surface-light dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-black rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : pkg ? "Update Package" : "Create Package"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Tag Modal Component
const TagModal: React.FC<{
  tag: ProfileTag | null;
  onClose: () => void;
  onSave: (tag: Partial<ProfileTag>) => void;
}> = ({ tag, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<ProfileTag>>({
    name: tag?.name || "",
    description: tag?.description || "",
    color: tag?.color || "#10B981",
    textColor: tag?.textColor || "#000000",
    icon: tag?.icon || "",
    sortOrder: tag?.sortOrder || 1,
    isActive: tag?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Preset colors for easy selection
  const presetColors = [
    { bg: "#10B981", text: "#000000", name: "Green" },
    { bg: "#3B82F6", text: "#FFFFFF", name: "Blue" },
    { bg: "#8B5CF6", text: "#FFFFFF", name: "Purple" },
    { bg: "#F59E0B", text: "#000000", name: "Amber" },
    { bg: "#EF4444", text: "#FFFFFF", name: "Red" },
    { bg: "#EC4899", text: "#FFFFFF", name: "Pink" },
    { bg: "#14B8A6", text: "#000000", name: "Teal" },
    { bg: "#6366F1", text: "#FFFFFF", name: "Indigo" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border-light dark:border-border-dark">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
              {tag ? "Edit Tag" : "Create New Tag"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Preview */}
          <div className="flex items-center justify-center p-4 bg-surface-light dark:bg-gray-800 rounded-lg">
            <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark mr-3">
              Preview:
            </span>
            <span
              className="px-4 py-1.5 rounded-full text-sm font-bold"
              style={{
                backgroundColor: formData.color,
                color: formData.textColor,
              }}
            >
              {formData.name || "Tag Name"}
            </span>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Tag Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., OG, Founding Member"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Brief description of this tag"
            />
          </div>

          {/* Color Presets */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
              Color Theme
            </label>
            <div className="grid grid-cols-4 gap-2">
              {presetColors.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      color: preset.bg,
                      textColor: preset.text,
                    })
                  }
                  className={`p-2 rounded-lg border-2 transition-all ${
                    formData.color === preset.bg
                      ? "border-primary scale-105"
                      : "border-transparent hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <div
                    className="w-full h-6 rounded"
                    style={{ backgroundColor: preset.bg }}
                  />
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1 block">
                    {preset.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Background Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="flex-1 px-3 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg text-sm"
                  placeholder="#10B981"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Text Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.textColor}
                  onChange={(e) =>
                    setFormData({ ...formData, textColor: e.target.value })
                  }
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.textColor}
                  onChange={(e) =>
                    setFormData({ ...formData, textColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg text-sm"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
              Sort Order
            </label>
            <input
              type="number"
              min={1}
              value={formData.sortOrder}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sortOrder: parseInt(e.target.value) || 1,
                })
              }
              className="w-full px-4 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
              Lower numbers appear first in the tag selection dropdown
            </p>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="tagIsActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="w-4 h-4 text-primary bg-surface-light dark:bg-gray-800 border-border-light dark:border-border-dark rounded focus:ring-primary"
            />
            <label
              htmlFor="tagIsActive"
              className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark"
            >
              Tag is active (available for use in offers)
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border-light dark:border-border-dark rounded-lg font-medium hover:bg-surface-light dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name?.trim()}
              className="flex-1 px-4 py-2 bg-primary text-black rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : tag ? "Update Tag" : "Create Tag"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AdminPage;
